import { useApolloClient, useMutation } from "@vue/apollo-composable";
import {
  CardEncryptionKeyDocument,
  CardPinAction,
  CardStatus,
  LostOrStolenStatus,
  CreateCardDocument,
  CustomerCardsDocument,
  ElevatedOperation,
  ManageCardPinDocument,
  OtpDeliveryChannel,
  ReportLostOrStolenCardDocument,
  RequestOtpDocument,
  UpdateCardStatusDocument,
  VerifyCardDocument,
  type CardEncryptionKeyQuery,
  type CreateCardInput,
} from "@/api/generated/graphql";
import { encryptCardCvv, encryptCardPin } from "@/utils/cardPinEncryption";
import { getGraphqlErrorCode } from "@/utils/graphqlErrors";

export const customerCardsQuery = CustomerCardsDocument;

export async function fetchCardEncryptionKey(type = "pin") {
  const { resolveClient } = useApolloClient();
  const client = resolveClient();
  const result = await client.query<CardEncryptionKeyQuery>({
    query: CardEncryptionKeyDocument,
    variables: { type },
    fetchPolicy: "network-only",
  });

  const encryptionKey = result.data?.cardEncryptionKey;
  if (!encryptionKey) {
    throw new Error("Card encryption key not available");
  }

  return encryptionKey;
}

export async function createCard(input: CreateCardInput) {
  const { resolveClient } = useApolloClient();
  const client = resolveClient();
  const result = await client.mutate({
    mutation: CreateCardDocument,
    variables: { input },
    // Refetch by operation name so the active CustomerCards query is refetched with
    // its current variables (account numbers). Passing { query } would refetch with
    // empty variables and miss the watched query, leaving the list stale.
    refetchQueries: ["CustomerCards"],
    awaitRefetchQueries: true,
  });

  return result.data?.createCard;
}

export async function requestPinOtp({ channel }: { channel: OtpDeliveryChannel }) {
  const { mutate } = useMutation(RequestOtpDocument);
  const res = await mutate({ operation: ElevatedOperation.SetCardPin, deliveryChannel: channel });
  return res?.data?.requestOtp === true;
}

export function otpChannelFromDeliveryMode(deliveryMode?: "sms" | "email"): OtpDeliveryChannel {
  return deliveryMode === "sms" ? OtpDeliveryChannel.Sms : OtpDeliveryChannel.Email;
}

export async function setCardPin({ token, pin, verificationCode }: { token: string; pin: string; verificationCode?: string }) {
  const { resolveClient } = useApolloClient();
  const client = resolveClient();
  const encryptionKey = await fetchCardEncryptionKey("pin");
  const { keyIdentifier, eNewPIN } = encryptCardPin(pin, encryptionKey);

  const result = await client.mutate({
    mutation: ManageCardPinDocument,
    variables: {
      input: {
        token,
        action: CardPinAction.Set,
        keyIdentifier,
        eNewPIN,
        ...(verificationCode ? { verificationCode } : {}),
      },
    },
  });

  return result.data?.manageCardPin;
}

export async function verifyCard({ token, cvv, expiryDate }: { token: string; cvv: string; expiryDate?: string }) {
  const { resolveClient } = useApolloClient();
  const client = resolveClient();
  const encryptionKey = await fetchCardEncryptionKey("cvv");
  const { keyIdentifier, eCvv } = encryptCardCvv(cvv, encryptionKey);

  const result = await client.mutate({
    mutation: VerifyCardDocument,
    variables: {
      input: {
        token,
        keyIdentifier,
        eCvv,
        ...(expiryDate ? { expiryDate } : {}),
      },
    },
    refetchQueries: ["CustomerCards"],
    awaitRefetchQueries: true,
  });

  return result.data?.verifyCard;
}

export async function updateCardStatus({ token, status }: { token: string; status: CardStatus }) {
  const { resolveClient } = useApolloClient();
  const client = resolveClient();
  const result = await client.mutate({
    mutation: UpdateCardStatusDocument,
    variables: {
      input: { token, status },
    },
  });

  const updated = result.data?.updateCardStatus;
  if (!updated) {
    throw new Error("updateCardStatus returned no card");
  }

  return updated;
}

export async function reportLostOrStolenCard({ token, status }: { token: string; status: LostOrStolenStatus }) {
  if (!token) {
    throw new Error("token is required");
  }
  if (!status) {
    throw new Error("status is required");
  }

  const { resolveClient } = useApolloClient();
  const client = resolveClient();
  try {
    const result = await client.mutate({
      mutation: ReportLostOrStolenCardDocument,
      variables: {
        input: { token, status },
      },
      refetchQueries: ["CustomerCards"],
      awaitRefetchQueries: true,
    });

    const reported = result.data?.reportLostOrStolenCard;
    if (!reported) {
      throw new Error("reportLostOrStolenCard returned no result");
    }

    return reported;
  } catch (error) {
    if (getGraphqlErrorCode(error) === "CARD_REPLACEMENT_FAILED") {
      await client.refetchQueries({ include: ["CustomerCards"] }).catch(() => undefined);
    }
    throw error;
  }
}
